import styled, { css } from 'styled-components'

import { Handler, HandlerOf } from '../../helpers/typeHelper'
import { ApiVersion } from '../../models'
import { createNote, deleteNote, NoteOrActivity, NoteRecordType, updateNote } from '../../models/note'
import { useUserProvider } from '../Context/UserContext'
import { InlineSpinner } from '../Spinner'
import { NOTES_GAP } from './Notes.constants'
import { NotesListHeader } from './NotesListHeader'
import { NotesListMessages } from './NotesListMessages'

interface NotesListProps {
  recordId: string
  recordType: NoteRecordType
  recordName?: string
  notes?: NoteOrActivity[]
  refetchNotes: Handler
  isNotesLoading: boolean
  isTimeline?: boolean
  showOnlyActivity: boolean
  setShowOnlyActivity: HandlerOf<boolean>
  filterTags: string[] | null
  setFilterTags: HandlerOf<string[] | null>
  isStatic: boolean
  apiVersion?: ApiVersion
  renderHeaderButton?: () => React.ReactNode
}

export function NotesList({
  recordId,
  recordType,
  recordName,
  isTimeline,
  showOnlyActivity,
  setShowOnlyActivity,
  filterTags,
  setFilterTags,
  refetchNotes,
  isNotesLoading,
  notes,
  isStatic,
  apiVersion,
  renderHeaderButton,
}: NotesListProps) {
  const { showBanner } = useUserProvider()

  async function handleAddNote(text: string, tags?: string[]) {
    try {
      await createNote({ recordId, recordType, payload: { text, tags }, apiVersion })
      await refetchNotes()
    } catch (err: any) {
      showBanner('error', err)
    }
  }

  async function handleUpdateNote(noteId: string, text: string, tags?: string[]) {
    try {
      await updateNote({ recordId, recordType, noteId, payload: { text, tags }, apiVersion })
      await refetchNotes()
    } catch (err: any) {
      showBanner('error', err)
    }
  }

  async function handleDeleteNote(noteId: string) {
    try {
      await deleteNote({ recordId, recordType, noteId, apiVersion })
      await refetchNotes()
    } catch (err: any) {
      showBanner('error', err)
    }
  }

  return (
    <OuterWrap>
      <Wrap $isStatic={isStatic}>
        <NotesListHeader
          recordId={recordId}
          recordName={recordName}
          recordType={recordType}
          isTimeline={isTimeline}
          handleAddNote={handleAddNote}
          showOnlyActivity={showOnlyActivity}
          setShowOnlyActivity={setShowOnlyActivity}
          filterTags={filterTags}
          setFilterTags={setFilterTags}
          apiVersion={apiVersion}
          renderHeaderButton={renderHeaderButton}
          isStatic={isStatic}
        />
        {isNotesLoading ? (
          <div>
            <InlineSpinner mt={4} mb={4} />
          </div>
        ) : (
          <NotesListMessages
            recordType={recordType}
            recordId={recordId}
            notes={notes}
            isTimeline={isTimeline}
            showOnlyActivity={showOnlyActivity}
            handleUpdateNote={handleUpdateNote}
            handleDeleteNote={handleDeleteNote}
            filterTags={filterTags}
            apiVersion={apiVersion}
            isStatic={isStatic}
          />
        )}
      </Wrap>
    </OuterWrap>
  )
}

const OuterWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  height: 100%;
  position: relative;
  width: 100%;
`

const Wrap = styled.div<{
  $isStatic: boolean
}>`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  overflow: hidden;
  position: absolute;
  top: 0;
  right: -${NOTES_GAP}px;
  left: -${NOTES_GAP}px;
  bottom: 0;
  padding: 0 ${NOTES_GAP}px;
  background: white;
  margin-top: -150px;
  padding-top: 150px;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;

  ${(p) =>
    p.$isStatic &&
    css`
      position: static;
      background: none;
      padding: 0;
      margin: 0;
    `}
`
